import { useState } from "react";
import { PDFViewer } from "@react-pdf/renderer";
import { ReportPDF } from "./ReportPDF";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X, AlertTriangle, XCircle, CheckCircle } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

interface ValidationResult {
  type: 'error' | 'warning';
  message: string;
}

function validateReportIntegrity(
  report: any,
  sessionMeta: any,
  commitHash?: string,
  committedAt?: string,
  reportId?: string
): ValidationResult[] {
  const results: ValidationResult[] = [];

  // 1. Governance completeness
  if (!commitHash) {
    results.push({ type: 'warning', message: 'Kein Commit-Hash vorhanden – Governance-Metadaten unvollständig.' });
  }
  if (!committedAt) {
    results.push({ type: 'warning', message: 'Kein Commit-Zeitstempel vorhanden.' });
  }
  if (!reportId) {
    results.push({ type: 'warning', message: 'Keine Report-ID vorhanden.' });
  }

  // 2. Empty critical sections
  const execSummary = report?.executive_summary;
  if (!execSummary || (!execSummary.intro_paragraph && !execSummary.executive_intro_paragraph && !execSummary.key_insights?.length)) {
    results.push({ type: 'error', message: 'Executive Summary ist leer – Report unvollständig.' });
  }
  if (!report?.recommendation || !report.recommendation.recommended_option_label) {
    results.push({ type: 'error', message: 'Keine Empfehlung vorhanden – Report unvollständig.' });
  }
  const opts = report?.options_overview?.options;
  if (!opts || !Array.isArray(opts) || opts.length === 0) {
    results.push({ type: 'error', message: 'Keine Optionen im Report – Report unvollständig.' });
  }

  // 3. Time logic consistency
  const roadmap = report?.action_roadmap;
  if (roadmap) {
    const validityDays = sessionMeta?.decision_commit?.validity_days || roadmap.validity_days;
    const weekCount = [roadmap.week_1, roadmap.week_2, roadmap.week_3, roadmap.week_4].filter(Boolean).length;
    if (validityDays && weekCount > 0) {
      const expectedWeeks = Math.ceil(validityDays / 7);
      if (weekCount > expectedWeeks + 1) {
        results.push({ type: 'warning', message: `Zeitlogik: ${weekCount} Wochen geplant bei ${validityDays} Tagen Gültigkeit.` });
      }
    }
  }

  // 4. Float formatting scan
  const reportStr = JSON.stringify(report);
  const floatMatches = reportStr.match(/\d+\.\d{3,}/g);
  if (floatMatches && floatMatches.length > 0) {
    const unique = [...new Set(floatMatches)].slice(0, 3);
    results.push({ type: 'warning', message: `Ungerundete Dezimalzahlen gefunden: ${unique.join(', ')}` });
  }

  return results;
}

interface ReportPDFPreviewProps {
  report: any;
  branding?: any;
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  sessionMeta?: any;
  // Governance props
  reportId?: string;
  versionNumber?: number;
  commitHash?: string;
  committedAt?: string;
  commitType?: string;
  inputsHash?: string;
  modelVersion?: string;
  isLocked?: boolean;
}

export const ReportPDFPreview = ({
  report,
  branding,
  isOpen,
  onClose,
  sessionId,
  sessionMeta,
  reportId,
  versionNumber,
  commitHash,
  committedAt,
  commitType,
  inputsHash,
  modelVersion,
  isLocked,
}: ReportPDFPreviewProps) => {
  const { toast } = useToast();
  const [pdfMode, setPdfMode] = useState<'board' | 'operational'>('board');
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(false);

  const pdfProps = {
    report,
    branding,
    sessionMeta,
    sessionId,
    commitHash,
    commitType,
    committedAt,
    reportId,
    versionNumber,
    inputsHash,
    modelVersion,
    mode: pdfMode,
  };

  const filename = `decision-report-${sessionId.substring(0, 8)}-v${versionNumber || 1}-${pdfMode}.pdf`;

  const executeDownload = async () => {
    try {
      toast({
        title: "PDF wird erstellt",
        description: "Bitte warten Sie einen Moment...",
      });

      const blob = await pdf(<ReportPDF {...pdfProps} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      toast({
        title: "PDF erstellt",
        description: `${pdfMode === 'board' ? 'Board' : 'Operational'}-Report als PDF heruntergeladen.`,
      });
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast({
        title: "Fehler",
        description: "Der PDF-Download ist fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async () => {
    // Pre-export validation
    const results = validateReportIntegrity(report, sessionMeta, commitHash, committedAt, reportId);
    const errors = results.filter(r => r.type === 'error');
    const warnings = results.filter(r => r.type === 'warning');

    if (errors.length > 0 || warnings.length > 0) {
      setValidationResults(results);
      setShowValidation(true);
      setPendingDownload(true);
      return;
    }

    await executeDownload();
  };

  const handleForceDownload = async () => {
    setShowValidation(false);
    setPendingDownload(false);
    await executeDownload();
  };

  const hasBlockingErrors = validationResults.some(r => r.type === 'error');

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-7xl h-[90vh] p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle>PDF-Vorschau</DialogTitle>
              <div className="flex items-center gap-2">
                {/* Board / Operational Toggle */}
                <div className="flex items-center rounded-md border border-border bg-muted/30 p-0.5">
                  <button
                    onClick={() => setPdfMode('board')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      pdfMode === 'board'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Board
                  </button>
                  <button
                    onClick={() => setPdfMode('operational')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      pdfMode === 'operational'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Operational
                  </button>
                </div>
                <Button onClick={handleDownload} size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Herunterladen
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <PDFViewer width="100%" height="100%" showToolbar={true}>
              <ReportPDF {...pdfProps} />
            </PDFViewer>
          </div>
        </DialogContent>
      </Dialog>

      {/* Validation Results Dialog */}
      <AlertDialog open={showValidation} onOpenChange={setShowValidation}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {hasBlockingErrors ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )}
              Report Integrity Check
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 mt-2">
                {validationResults.filter(r => r.type === 'error').length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-destructive uppercase tracking-wide">Fehler (blockierend)</p>
                    {validationResults.filter(r => r.type === 'error').map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 rounded border border-destructive/30 bg-destructive/5">
                        <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{r.message}</span>
                      </div>
                    ))}
                  </div>
                )}
                {validationResults.filter(r => r.type === 'warning').length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Warnungen (übergehbar)</p>
                    {validationResults.filter(r => r.type === 'warning').map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 rounded border border-amber-500/30 bg-amber-500/5">
                        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{r.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowValidation(false); setPendingDownload(false); }}>
              Abbrechen
            </AlertDialogCancel>
            {!hasBlockingErrors && (
              <AlertDialogAction onClick={handleForceDownload}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Trotzdem exportieren
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
